import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useForm from 'hooks/useForm';
import { updateWorker, setOnboarding } from 'modules/state-mgmt/onboarding';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { useWizardStepNavigation } from 'modules/views/shared/Wizard';
import { enqueueSnackbar } from 'notistack';
import { extractCustomFieldCodes, formatBackendValidationErrors, getBackendErrorsFromResponse } from 'utils';
import tradesSchema from '../schema';
import { ITradesValues } from '../types';
import { useTradesData } from './useTradesData';
import { useTradesFields, TRADES_FIELD_CONFIG } from './useTradesFields';
import { useOtherTrades } from './useOtherTrades';
import { ROUTES } from 'constants/routes';

export const useTrades = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { goToNextStep, goToPreviousStep, canGoToPreviousStep, getNextStepName } = useWizardStepNavigation();
  const { isOffline, isWorker, uploadingDetails } = useAppSelector(state => ({
    isOffline: !state.app.isOnline || state.onboarding?.uploadingDetails?.isOffline,
    isWorker: !state.user?.userId || state.user?.isWorker,
    uploadingDetails: state.onboarding?.uploadingDetails,
  }));

  const [isLoading, setIsLoading] = useState(false);

  const {
    languageCode,
    profileFieldConstraintsMap,
    masterListTrades,
    nonMasterListTrades,
    otherTrades,
    workerId,
    skilledTrades,
    trades,
    isSupervisor,
    isAffiliatedToLaborUnion,
    isSkilled,
    laborUnionNumber,
    hasReachedConsentForm,
    isArchivedProject,
  } = useTradesData();

  const transformTrades = (beTrades, skilledTrades) => {
    const trades = [];
    const otherTradesList = [];
    beTrades.forEach(trade => {
      const matchedTrade = skilledTrades.find(skilledTrade => skilledTrade.id === trade.id) || null;
      if (!matchedTrade) {
        otherTradesList.push({ OtherText: trade.name, isSaved: false });
      } else {
        if (matchedTrade?.masterList) {
          trades?.push(matchedTrade.id);
        } else {
          otherTradesList.push({ OtherText: matchedTrade.id, isSaved: true });
        }
      }
    });
    return { trades, otherTradesList };
  };

  // Form initial values
  const initialValues: ITradesValues = useMemo(() => {
    const transformedTrades = transformTrades(trades || [], skilledTrades[languageCode] || []);
    return {
      isSupervisor: isSupervisor ?? null,
      isAffiliatedToLaborUnion: isAffiliatedToLaborUnion ?? null,
      isSkilled: isSkilled ?? null,
      laborUnionNumber: laborUnionNumber || '',
      trades: transformedTrades.trades || [],
      otherTradesList: transformedTrades.otherTradesList || [],
    };
  }, [skilledTrades, languageCode, trades, isSupervisor, isAffiliatedToLaborUnion, isSkilled, laborUnionNumber]);

  // Form validation schema
  const validationSchema = useMemo(
    () => tradesSchema(t, profileFieldConstraintsMap, hasReachedConsentForm, otherTrades?.id),
    [t, profileFieldConstraintsMap, hasReachedConsentForm, otherTrades?.id],
  );

  const hasPendingUploadErrorForPage = uploadingDetails?.errors?.pageUrl === ROUTES.TRADES.path;

  // Form submission handler
  const onSubmit = async (formValues: ITradesValues) => {
    // Nothing was edited on this page — no worker update needed, online or offline.
    // A failed background upload for this page still needs a retry, so it forces the write.
    if (!getIsDirty() && !hasPendingUploadErrorForPage) {
      goToNextStep();
      return;
    }

    if (formValues.isAffiliatedToLaborUnion !== true) {
      formValues.laborUnionNumber = null;
    }

    if (isArchivedProject) {
      goToNextStep();
      return;
    }

    if (isOffline && !isWorker) {
      // Explicit Offline Flow (Middleware handles persistence)
      const resolvedTrades = [];

      // 1. Master List Trades (IDs -> Objects). masterListTrades leaves out "Other", so keep it explicitly.
      formValues.trades.forEach(tradeId => {
        const tradeObj =
          masterListTrades.find(t => t.id === tradeId) || (tradeId === otherTrades?.id ? otherTrades : null);
        if (tradeObj) resolvedTrades.push(tradeObj);
      });

      // 2. Other Trades (Mixed -> Objects)
      formValues.otherTradesList.forEach(item => {
        if (item.isSaved) {
          // item.OtherText is ID
          const tradeObj = nonMasterListTrades.find(t => t.id === item.OtherText); // or check all skilledTrades
          if (tradeObj) resolvedTrades.push(tradeObj);
        } else {
          // item.OtherText is Name
          resolvedTrades.push({ name: item.OtherText });
        }
      });

      const updatedState = {
        ...formValues,
        trades: resolvedTrades,
        uploadingDetails: {
          ...(uploadingDetails || {}),
          isOffline: true,
          pendingUploads: { ...(uploadingDetails?.pendingUploads || {}), trades: true },
          errors:
            uploadingDetails?.errors?.pageUrl === ROUTES.TRADES.path
              ? { pageUrl: '', errorObject: null }
              : uploadingDetails?.errors,
        },
      };

      // Remove UI-specific fields that shouldn't persist if they conflict (optional)
      delete (updatedState as any).otherTradesList;

      await dispatch(setOnboarding(updatedState));

      goToNextStep();
      return;
    }

    try {
      setIsLoading(true);

      // Extract custom field codes for Trades screen
      const workerCustomFieldCodesToValidate = extractCustomFieldCodes(TRADES_FIELD_CONFIG);

      const res = await dispatch(
        updateWorker({
          workerId,
          data: {
            ...formValues,
            trades: (() => {
              const tradesWithoutOther = (formValues.trades || []).filter(id => id !== otherTrades?.id);
              const hasOtherTrades = (formValues.otherTradesList || []).length > 0;
              return hasOtherTrades && otherTrades?.id ? [...tradesWithoutOther, otherTrades.id] : tradesWithoutOther;
            })(),
          },
          languageCode,
          workerCustomFieldCodesToValidate,
        }),
      );

      if (res.type.includes('fulfilled')) {
        goToNextStep();
      } else {
        // Handle backend validation errors - show all errors in snackbar
        const errorMessage = formatBackendValidationErrors(res.payload);
        enqueueSnackbar('Failed to update trades. Please try again.', { variant: 'error' });
        enqueueSnackbar(errorMessage, {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
      }
    } catch (error) {
      console.error('Error submitting trades form:', error);
      enqueueSnackbar('An error occurred while updating trades.', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const { values, errors, handleSubmit, setValue, setErrors, getIsDirty } = useForm<ITradesValues>({
    initialValues,
    validationSchema: validationSchema as any,
    ignoreValidations: isArchivedProject,
    onSubmit,
  });

  // Field configurations using custom hook
  const {
    roleFields,
    tradeFields,
    otherTradeFields,
    isRoleFieldsSectionVisible,
    isTradeFieldsSectionVisible,
    fieldDisplayNames,
    getDynamicFormFieldMap,
  } = useTradesFields({
    profileFieldConstraintsMap,
    languageCode,
    values,
    masterListTrades,
    nonMasterListTrades,
    setValue: (field, value) => setValue(field, value),
    onOtherTradeRemove: tradeToRemove => {
      const currentTrades = values?.otherTradesList || [];
      const updatedTrades = currentTrades.filter(trade => trade.OtherText !== tradeToRemove.OtherText);
      setValue('otherTradesList', updatedTrades);
    },
    hasReachedConsentForm,
  });

  // Other trades management
  const { isOtherTradeSelected, handleToggleOtherTrade } = useOtherTrades({
    values,
    setValue,
    otherTrades,
  });

  // On mount, populate form errors from background upload errors (if any)
  useEffect(() => {
    const uploadErrors = uploadingDetails?.errors;
    if (uploadErrors?.pageUrl === ROUTES.TRADES.path && uploadErrors?.errorObject) {
      const tradesFields = [
        'isSupervisor',
        'isAffiliatedToLaborUnion',
        'isSkilled',
        'laborUnionNumber',
        'trades',
        'otherTradesList',
      ];
      const errorFieldMapper = {
        isSupervisor: 'role',
        isAffiliatedToLaborUnion: 'laborUnionStatus',
        isSkilled: 'workExperience',
      };
      const backendErrors = getBackendErrorsFromResponse(uploadErrors.errorObject, tradesFields, errorFieldMapper);
      if (backendErrors) {
        setErrors(backendErrors);
      }
    }
  }, []);

  // Navigation handlers
  const handleBack = () => goToPreviousStep();
  const handleNext = () => handleSubmit();

  return {
    // State
    isLoading,
    values,
    errors,

    // Field configurations
    roleFields,
    tradeFields,
    otherTradeFields,
    isRoleFieldsSectionVisible,
    isTradeFieldsSectionVisible,

    // Other trades
    isOtherTradeSelected,
    otherTradesId: otherTrades?.id,

    // Handlers
    handleBack,
    handleNext,
    handleToggleOtherTrade,
    setValue,
    canGoToPreviousStep,
    getNextStepName,

    // Skippable fields support
    fieldDisplayNames,
    getDynamicFormFieldMap,
    profileFieldConstraintsMap,
    hasReachedConsentForm,
    isArchivedProject,
  };
};
